import { CartUpdateEvent } from '@theme/events';

/**
 * Bridges Monster Cart (app embed) cart changes to the theme.
 *
 * Monster renders its own drawer and mutates the cart (quantity, remove, upsells)
 * without dispatching theme events, so the header cart bubble goes stale.
 * Monster fires `cart:updated` on `document` after every change; we translate it
 * into the theme's `cart:update` event so `<cart-icon>` (and the cart page) re-render.
 */
const MONSTER_EVENT = 'cart:updated';
const SOURCE = 'monster-cart';

/** @type {number | null} */
let lastCount = null;

/**
 * @param {unknown} items
 * @returns {number | null}
 */
function countFromItems(items) {
  if (!Array.isArray(items)) return null;
  return items.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);
}

/**
 * @returns {Promise<{ item_count: number } | null>}
 */
async function fetchCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return response.ok ? response.json() : null;
  } catch {
    return null;
  }
}

/**
 * @param {Event} event
 */
async function onMonsterCartUpdated(event) {
  const detail = /** @type {CustomEvent} */ (event).detail;
  let itemCount = countFromItems(detail?.items);
  let cart = detail ?? null;

  if (itemCount === null) {
    cart = await fetchCart();
    if (!cart) return;
    itemCount = cart.item_count;
  }

  if (itemCount === lastCount) return;
  lastCount = itemCount;

  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { itemCount, source: SOURCE }));
}

document.addEventListener(MONSTER_EVENT, onMonsterCartUpdated);
